import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      id: 'banking',
      type: 'Financial Services',
      tag: 'Financial Services',
      tagColor: 'purple',
      title: 'How a Tier-1 Financial Institution Gained Visibility Into Its Data Infrastructure',
      problem: 'With hundreds of data tables feeding critical regulatory reports and customer-facing dashboards, the institution had no systematic way to detect data quality issues. Problems were discovered after reports broke — sometimes days later.',
      solution: 'Deployed Qupid on-premise across core data sources with 20+ active observers monitoring schema changes, data freshness, and volume anomalies in real-time.',
      results: [
        'Real-time monitoring across critical enterprise data sources',
        'Automated detection of schema changes and data anomalies',
        'BI Discovery mapping dashboards to upstream data dependencies',
        'Proactive alerting before downstream reports are impacted'
      ],
      deployment: 'On-premise within the client\'s existing data center infrastructure. Zero data sent externally.'
    },
    {
      id: 'telco',
      type: 'Telecommunications',
      tag: 'Telco',
      tagColor: 'teal',
      title: 'A National Telco Operator Builds Trust in Customer Analytics Data',
      problem: 'Revenue assurance and customer analytics depended on data pipelines that broke silently. The data team spent significant time on manual quality checks instead of building new capabilities.',
      solution: 'Implemented Qupid to monitor data pipelines feeding customer analytics and revenue reporting, with automated alerting on freshness SLA breaches and distribution drift.',
      results: [
        'Continuous monitoring of high-volume telecom data pipelines',
        'Automated freshness and completeness checks on critical tables',
        'Drift detection identifying subtle changes in data distributions',
        'Engineering team redirected from firefighting to feature development'
      ],
      deployment: 'On-premise deployment integrated with existing data warehouse and BI stack.'
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-medium rounded-full mb-4">
            Case Studies
          </span>
          <h1 className="text-5xl font-bold mb-4 text-gray-900">
            Real results from real deployments
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            See how leading organizations use Qupid to build trust
            in their data and catch issues before they impact the business.
          </p>
        </motion.div>

        {/* Case Studies */}
        <div className="space-y-12">
          {caseStudies.map((study, index) => (
            <motion.article
              key={study.id}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="p-8 lg:p-10">
                {/* Tag */}
                <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full mb-4 ${
                  study.tagColor === 'purple'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-teal-100 text-teal-700'
                }`}>
                  {study.tag}
                </span>

                <h2 className="text-2xl font-bold text-gray-900 mb-6">{study.title}</h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left: Problem + Solution */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">The Challenge</h3>
                      <p className="text-gray-600 leading-relaxed">{study.problem}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">The Solution</h3>
                      <p className="text-gray-600 leading-relaxed">{study.solution}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Deployment</h3>
                      <div className="flex items-start gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <p className="text-gray-600 text-sm">{study.deployment}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right: Results */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Results</h3>
                    <div className="space-y-3">
                      {study.results.map((result, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                            study.tagColor === 'purple' ? 'bg-purple-100' : 'bg-teal-100'
                          }`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${
                              study.tagColor === 'purple' ? 'text-purple-600' : 'text-teal-600'
                            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{result}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center bg-gray-50 rounded-2xl p-10 border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            See what Qupid can do for your data
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Schedule a demo and we'll show you how data observability works
            with your specific data infrastructure.
          </p>
          <Link to="/#request-demo" className="button button-primary inline-block">
            Request a Demo
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CaseStudiesPage;
